import React, { useCallback, useEffect, useState } from 'react';
import { api, mutate } from '../../shared/api';
import { Btn, Card, DataTable, Pill, Select, type Col } from '../../shared/ui';
import { Field, FormGrid, Input, Note, Section, day, eur, num, tone, type Intermediary, type Props } from './posrShared';

/** m.1.2 Cjenik provizije, tromjesecna ocjena posrednika i kanal Posrednik. */

type PriceList = { id: number; label: string; valid_from: string; valid_to: string | null; items: number };
type PriceItem = { id: number; id_artikla: number; sku: string | null; naziv: string | null; standard_ruc_eur: string | null; amount_eur: string };

export function PosrPriceLists({ token, isAdmin, onError }: Props) {
    const [rows, setRows] = useState<PriceList[] | null>(null);
    const [items, setItems] = useState<PriceItem[] | null>(null);
    const [selId, setSelId] = useState<number | null>(null);
    const [label, setLabel] = useState('');
    const [from, setFrom] = useState('');
    const [text, setText] = useState('');
    const [busy, setBusy] = useState(false);

    const load = useCallback(() => {
        api<{ rows: PriceList[] }>('/posrednici/price-lists', token).then((d) => setRows(d.rows)).catch((e: Error) => { onError(e.message); setRows([]); });
    }, [token, onError]);
    useEffect(load, [load]);

    const open = (p: PriceList) => {
        setSelId(p.id); setItems(null);
        api<{ rows: PriceItem[] }>(`/posrednici/price-lists/${p.id}/items`, token).then((d) => setItems(d.rows)).catch((e: Error) => onError(e.message));
    };

    // Stavke se lijepe iz tablice, jedna po retku: id artikla, naziv, standardni RUC, provizija EUR (tab ili tocka-zarez).
    const parse = () => text.split('\n').map((l) => l.trim()).filter(Boolean).map((l) => {
        const p = l.split(/\t|;/).map((x) => x.trim());
        const n = (s: string | undefined) => (s ? Number(s.replace(/\./g, '').replace(',', '.')) : null);
        return { id_artikla: Number(p[0]), naziv: p[1] || null, standard_ruc_eur: n(p[2]), amount_eur: n(p[3]) };
    });

    const save = async () => {
        setBusy(true);
        try {
            await mutate('/posrednici/price-lists', token, 'POST', { label, valid_from: from, items: parse() });
            setLabel(''); setFrom(''); setText(''); load();
        } catch (e) { onError((e as Error).message); } finally { setBusy(false); }
    };

    const cols: Col<PriceList>[] = [
        { key: 'label', label: 'Cjenik' },
        { key: 'valid_from', label: 'Vrijedi od', render: (r) => day(r.valid_from) },
        { key: 'valid_to', label: 'Vrijedi do', render: (r) => day(r.valid_to) },
        { key: 'items', label: 'Stavki', align: 'right' },
    ];
    const itemCols: Col<PriceItem>[] = [
        { key: 'id_artikla', label: 'ID artikla' },
        { key: 'naziv', label: 'Naziv', render: (r) => r.naziv ?? '-' },
        { key: 'standard_ruc_eur', label: 'Standardni RUC', align: 'right', render: (r) => eur(r.standard_ruc_eur) },
        { key: 'amount_eur', label: 'Provizija po komadu', align: 'right', render: (r) => <strong>{eur(r.amount_eur)}</strong> },
    ];

    return (
        <Card>
            <Section title="Cjenik provizije po artiklu">
                <Note>Iznos je fiksan u eurima po komadu, bez PDV-a, oko petine standardnog RUC-a artikla. Kupnja artikla kojeg nema u cjeniku ne nosi proviziju dok se cjenik ne dopuni.</Note>
                <DataTable cols={cols} rows={rows} onRowClick={open} empty="Cjenik još nije upisan." />
                {selId ? <DataTable cols={itemCols} rows={items} empty="Cjenik nema stavki." /> : null}
                {isAdmin ? (
                    <Card pad={14}>
                        <FormGrid>
                            <Field label="Naziv cjenika"><Input value={label} onChange={setLabel} placeholder="npr. Cjenik 2027-Q1" /></Field>
                            <Field label="Vrijedi od"><Input type="date" value={from} onChange={setFrom} /></Field>
                        </FormGrid>
                        <div style={{ marginTop: 10 }}>
                            <Field label="Stavke, jedna po retku, kao id artikla, naziv, standardni RUC i provizija (zalijepiti iz tablice)" wide>
                                <textarea value={text} onChange={(e) => setText(e.target.value)} rows={6} style={{ width: '100%', boxSizing: 'border-box', borderRadius: 10, border: '1px solid #e7e9f0', padding: 10, fontFamily: 'monospace', fontSize: '0.82rem' }} />
                            </Field>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 10 }}>
                            <Btn variant="primary" disabled={busy || !label || !from} onClick={save}>Upiši cjenik ({parse().length} stavki)</Btn>
                        </div>
                    </Card>
                ) : null}
            </Section>
        </Card>
    );
}

type Score = {
    id: number; intermediary_name: string; quarter: string; pts_conversion: string; pts_ruc_per_lead: string; pts_retention: string;
    pts_documentation: string; pts_quick_return: string; total: string; grade: string;
};

function currentQuarter(): string {
    const d = new Date();
    return `${d.getFullYear()}-Q${Math.floor(d.getMonth() / 3) + 1}`;
}

export function PosrScores({ token, onError }: Props) {
    const [rows, setRows] = useState<Score[] | null>(null);
    const [intermediaries, setIntermediaries] = useState<Intermediary[]>([]);
    const [iid, setIid] = useState('');
    const [quarter, setQuarter] = useState(currentQuarter());
    const [doc, setDoc] = useState('');
    const [busy, setBusy] = useState(false);

    const load = useCallback(() => {
        api<{ rows: Score[] }>('/posrednici/scores', token).then((d) => setRows(d.rows)).catch((e: Error) => { onError(e.message); setRows([]); });
    }, [token, onError]);
    useEffect(load, [load]);
    useEffect(() => {
        api<{ rows: Intermediary[] }>('/posrednici/intermediaries', token).then((d) => setIntermediaries(d.rows)).catch(() => undefined);
    }, [token]);

    const compute = async () => {
        setBusy(true);
        try {
            await mutate('/posrednici/scores', token, 'POST', {
                intermediary_id: Number(iid), quarter, documentation_share: doc === '' ? null : Number(doc.replace(',', '.')) / 100,
            });
            load();
        } catch (e) { onError((e as Error).message); } finally { setBusy(false); }
    };

    const cols: Col<Score>[] = [
        { key: 'quarter', label: 'Tromjesečje' },
        { key: 'intermediary_name', label: 'Posrednik' },
        { key: 'pts_conversion', label: 'Konverzija /30', align: 'right', render: (r) => num(r.pts_conversion, 1) },
        { key: 'pts_ruc_per_lead', label: 'RUC po leadu /25', align: 'right', render: (r) => num(r.pts_ruc_per_lead, 1) },
        { key: 'pts_retention', label: 'Zadržavanje /15', align: 'right', render: (r) => num(r.pts_retention, 1) },
        { key: 'pts_documentation', label: 'Urednost /20', align: 'right', render: (r) => num(r.pts_documentation, 1) },
        { key: 'pts_quick_return', label: 'Brzi povrat /10', align: 'right', render: (r) => num(r.pts_quick_return, 1) },
        { key: 'total', label: 'Ukupno', align: 'right', render: (r) => <strong>{num(r.total, 1)}</strong> },
        { key: 'grade', label: 'Razred', render: (r) => <Pill tone={tone(r.grade)}>{r.grade}</Pill> },
    ];

    return (
        <Card>
            <Section title="Tromjesečna ocjena posrednika" actions={<>
                <Select value={iid} onChange={setIid}>
                    <option value="">Posrednik</option>
                    {intermediaries.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
                </Select>
                <Input value={quarter} onChange={setQuarter} placeholder="2026-Q4" />
                <Input value={doc} onChange={setDoc} placeholder="Urednost KYC %" />
                <Btn variant="primary" disabled={busy || !iid} onClick={compute}>Izračunaj</Btn>
            </>}>
                <Note>
                    Razred A je 70 bodova i više, B od 50 do 69, a C ispod 50. Urednost dokumentacije BIS još ne zna izračunati iz KYC-a, pa se upisuje kao postotak klijenata s KYC-om bez dopune.
                    Brzi povrat daje punih 10 bodova ispod 5 posto vraćenog zlata, a bodovi padaju linearno do nule kod 25 posto.
                </Note>
                <DataTable cols={cols} rows={rows} empty="Nema izračunatih ocjena." />
            </Section>
        </Card>
    );
}

type MonthRow = { month: string; clients: number; promet_eur: number; ruc_eur: number; commission_eur: number; ruc_after_commission_eur: number };
type IntRow = { intermediary_id: number; intermediary_name: string; clients: number; promet_eur: string; ruc_eur: string; commission_eur: string };

export function PosrChannel({ token, onError }: Props) {
    const [data, setData] = useState<{ by_month: MonthRow[]; by_intermediary: IntRow[] } | null>(null);
    useEffect(() => {
        api<{ by_month: MonthRow[]; by_intermediary: IntRow[] }>('/posrednici/channel', token).then(setData).catch((e: Error) => onError(e.message));
    }, [token, onError]);

    const monthCols: Col<MonthRow & { id: string }>[] = [
        { key: 'month', label: 'Mjesec' },
        { key: 'clients', label: 'Kupaca', align: 'right' },
        { key: 'promet_eur', label: 'Promet', align: 'right', render: (r) => eur(r.promet_eur) },
        { key: 'ruc_eur', label: 'RUC', align: 'right', render: (r) => eur(r.ruc_eur) },
        { key: 'commission_eur', label: 'Provizija', align: 'right', render: (r) => eur(r.commission_eur) },
        { key: 'ruc_after_commission_eur', label: 'RUC nakon provizije', align: 'right', render: (r) => <strong>{eur(r.ruc_after_commission_eur)}</strong> },
    ];
    const intCols: Col<IntRow & { id: number }>[] = [
        { key: 'intermediary_name', label: 'Posrednik' },
        { key: 'clients', label: 'Kupaca', align: 'right' },
        { key: 'promet_eur', label: 'Promet', align: 'right', render: (r) => eur(r.promet_eur) },
        { key: 'ruc_eur', label: 'RUC', align: 'right', render: (r) => eur(r.ruc_eur) },
        { key: 'commission_eur', label: 'Provizija', align: 'right', render: (r) => eur(r.commission_eur) },
        { key: 'share', label: 'Udio u RUC-u', align: 'right', render: (r) => Number(r.ruc_eur) > 0 ? `${num((Number(r.commission_eur) / Number(r.ruc_eur)) * 100, 1)} %` : '-' },
    ];

    return (
        <Card>
            <Section title="Kanal Posrednik">
                <Note>Promet i RUC kupnji pripisanih posrednicima, bez storniranih stavki. Prodavač ostaje onaj koji je ponudu izvršio; podjela RUC-a nakon provizije između ADO-a i ADMS-a još nije odlučena i ovdje se ne računa.</Note>
                <DataTable cols={monthCols} rows={data ? data.by_month.map((r) => ({ ...r, id: r.month })) : null} empty="Kanal još nema kupnji." />
                <DataTable cols={intCols} rows={data ? data.by_intermediary.map((r) => ({ ...r, id: r.intermediary_id })) : null} empty="Nema podataka po posredniku." />
            </Section>
        </Card>
    );
}
